import { Component, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { DeprecatedModalComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { QueryFetcher } from '@app/models/core/query-fetcher.model';
import { Employee } from '@app/models/employee/employee.model';
import { TaskTemplate } from '@app/models/tasks/task-template.model';
import { Task } from '@app/models/tasks/task.model';
import { IndexView } from '@app/modules/common/views';
import { TaskChecklistComponent } from '@app/modules/employees/components/task-checklist/task-checklist.component';
import { AssignTaskTemplatesService } from '@app/modules/employees/services';
import { AnalyticService, TasksService } from '@app/services';
import { Translatable } from '@app/types/translatable.type';

const RESULTS_PER_PAGE = 25;

const isScrollContainerAtBottom = (element: HTMLElement): boolean => {
    const itemHeight = element.querySelector('.task').clientHeight;
    return element.scrollTop + element.offsetHeight >= element.scrollHeight - itemHeight;
};

type EmployeeTasksFilterType = 'All Tasks' | 'Assigned to Self' | 'Created for Self';

@Component({
    templateUrl: './employee-tasks-index.template.html',
    styleUrls: ['./employee-tasks-index.style.scss'],
})
export class EmployeeTasksIndexView extends IndexView implements OnInit {
    @ViewChild('applyTasksModal', { static: true }) applyTasksModal: DeprecatedModalComponent;
    @ViewChild('taskChecklist') taskChecklist: TaskChecklistComponent;

    isLoading = false;

    isLoadingTasks = false;

    employee: Employee;
    tasks: Task[] = [];

    segmentTitle: Translatable = 'employees.employee-tasks.tasks';

    taskFilterType: EmployeeTasksFilterType = 'All Tasks';

    totalTasks = 0;
    currentPageOfTasksContainer = 0;
    shouldConcat = false;

    showApplyTaskButton = false;

    generalTaskTemplates: TaskTemplate[] = [];
    autoMatchedTaskTemplates: TaskTemplate[] = [];
    otherTaskTemplates: TaskTemplate[] = [];

    constructor(
        protected route: ActivatedRoute,
        private router: Router,
        private assignTasksService: AssignTaskTemplatesService,
        private analyticService: AnalyticService,
        private tasksService: TasksService
    ) {
        super();
        this.employee = this.route.snapshot.data.models.get('employee');

        this.segmentTitle = {
            key: 'employees.employee-tasks.employeesTasks',
            params: { firstName: this.employee.firstName },
        };

        this.tasksService
            .onLoading()
            .pipe(takeUntilDestroyed())
            .subscribe((isLoading) => (this.isLoadingTasks = isLoading));
    }

    ngOnInit(): void {
        this.isLoading = true;

        Promise.all([
            this.getAllTasks(),
            this.setGeneralTaskTemplates(),
            this.setAutoMatchedTaskTemplates(),
            this.setOtherTaskTemplates(),
        ])
            .then(() => {
                this.showApplyTaskButton =
                    !!this.generalTaskTemplates.length ||
                    !!this.autoMatchedTaskTemplates.length ||
                    !!this.otherTaskTemplates.length;

                this.isLoading = false;
            })
            .catch(() => (this.isLoading = false));
    }

    async onToggleTaskFilter(taskFilterType: EmployeeTasksFilterType): Promise<void> {
        switch (taskFilterType) {
            case 'All Tasks':
                this.currentPageOfTasksContainer = 0;
                this.shouldConcat = false;

                this.getAllTasks();
                break;
            case 'Assigned to Self':
                this.currentPageOfTasksContainer = 0;
                this.shouldConcat = false;

                this.getTasksAssignedToSelf();
                break;
            case 'Created for Self':
                this.currentPageOfTasksContainer = 0;
                this.shouldConcat = false;

                this.getTasksCreatedForSelf();
                break;
        }
    }

    addNewTask(): void {
        this.router.navigate(['new'], { relativeTo: this.route });
    }

    showApplyTasksModal(): void {
        this.applyTasksModal.show();
    }

    updateTasks(): void {
        this.currentPageOfTasksContainer = 0;
        this.shouldConcat = false;

        this.getAllTasks();
    }

    onScrollTasksList(): void {
        const element = this.taskChecklist.container.nativeElement;

        const shouldLoadMoreTasks =
            this.tasks.length &&
            !this.isLoadingTasks &&
            isScrollContainerAtBottom(element) &&
            this.currentPageOfTasksContainer * RESULTS_PER_PAGE <= this.totalTasks;

        if (!shouldLoadMoreTasks) {
            return;
        }

        this.shouldConcat = true;
        this.getAllTasks();
    }

    onCancel(): void {}

    onApplyTaskTemplates(selectedTaskTemplates: TaskTemplate[]): void {
        this.isLoading = true;

        this.assignTasksService
            .onSave(selectedTaskTemplates, [this.employee])
            .then(() => {
                this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileTasksApplyTaskTemplate);
                this.updateTasks();
                this.isLoading = false;
            })
            .catch(() => (this.isLoading = false));
    }

    onDelete(): void {
        this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileTaskDeleted);
    }

    onReminded(): void {
        this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileTaskReminded);
    }

    private async getAllTasks(): Promise<void> {
        this.isLoadingTasks = true;

        const query = this.baseQuery.clone();

        const [allTasks, meta] = await query
            .page(this.currentPageOfTasksContainer + 1)
            .where('isAssigneeOrSubject', this.employee.id)
            .get();

        this.currentPageOfTasksContainer = meta.pagination.page;
        this.totalTasks = meta.pagination.total;

        this.tasks = this.shouldConcat ? [...this.tasks, ...allTasks] : allTasks;

        this.isLoadingTasks = false;
    }

    private async getTasksAssignedToSelf(): Promise<void> {
        this.isLoadingTasks = true;

        const query = this.baseQuery.clone();

        const [tasksAssignedToSelf, meta] = await query
            .page(this.currentPageOfTasksContainer + 1)
            .where('assigneeId', this.employee.id)
            .get();

        this.currentPageOfTasksContainer = meta.pagination.page;
        this.totalTasks = meta.pagination.total;

        this.tasks = this.shouldConcat ? [...this.tasks, ...tasksAssignedToSelf] : tasksAssignedToSelf;

        this.isLoadingTasks = false;
    }

    private async getTasksCreatedForSelf(): Promise<void> {
        this.isLoadingTasks = true;

        const query = this.baseQuery.clone();

        const [tasksCreatedForSelf, meta] = await query
            .page(this.currentPageOfTasksContainer + 1)
            .where('subjectId', this.employee.id)
            .get();

        this.currentPageOfTasksContainer = meta.pagination.page;
        this.totalTasks = meta.pagination.total;

        this.tasks = this.shouldConcat ? [...this.tasks, ...tasksCreatedForSelf] : tasksCreatedForSelf;

        this.isLoadingTasks = false;
    }

    private async setGeneralTaskTemplates(): Promise<void> {
        const [generalTaskTemplates] = await TaskTemplate.where('taskType', 'general').all();
        this.generalTaskTemplates = generalTaskTemplates;
    }

    private async setAutoMatchedTaskTemplates(): Promise<void> {
        const [autoMatchedTaskTemplates] = await TaskTemplate.where('filterMatching', this.employee.id).all();
        this.autoMatchedTaskTemplates = autoMatchedTaskTemplates;
    }

    private async setOtherTaskTemplates(): Promise<void> {
        const [otherTaskTemplates] = await TaskTemplate.where('filterNonMatching', this.employee.id).all();
        this.otherTaskTemplates = otherTaskTemplates;
    }

    private get baseQuery(): QueryFetcher {
        return Task.param('employee', this.employee.id).with([
            'subject',
            'subject.manager',
            'assignees',
            'assignees.manager',
            'subtasks',
            'file',
            'employeeDocument',
            'completedBy',
            'subtasksCount',
        ]);
    }
}
